/**
 * Normalize Builder.io blog `publishedDate` values for sorting.
 * CMS/API ordering can disagree with chronological order when formats mix
 * (ISO vs locale strings vs month names) — string sorts put "Feb" before "Mar", etc.
 */

/** Missing or unparseable dates sort last for "newest first" (desc). */
const MISSING = Number.NEGATIVE_INFINITY;

/**
 * Parse a raw publishedDate from Builder or from mapped card data.
 */
export function getPublishedDateMsFromRaw(raw: unknown): number {
  if (raw == null || raw === '') {
    return MISSING;
  }
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw > 1e12 ? raw : raw * 1000;
  }
  const s = String(raw).trim();
  if (!s) {
    return MISSING;
  }
  let t = Date.parse(s);
  if (!Number.isNaN(t)) {
    return t;
  }
  t = new Date(s).getTime();
  return Number.isNaN(t) ? MISSING : t;
}

export function getPublishedDateMsFromBlogEntry(entry: { data?: { publishedDate?: unknown } } | null | undefined): number {
  return getPublishedDateMsFromRaw(entry?.data?.publishedDate);
}

/**
 * Stable chronological sort for blog API rows (mutates order via copy).
 */
export function sortBlogEntriesByPublishedDate(entries: unknown[], order: 'desc' | 'asc'): unknown[] {
  return [...entries].sort((a, b) => {
    const ta = getPublishedDateMsFromBlogEntry(a as { data?: { publishedDate?: unknown } });
    const tb = getPublishedDateMsFromBlogEntry(b as { data?: { publishedDate?: unknown } });
    const primary = order === 'desc' ? tb - ta : ta - tb;
    if (primary !== 0) {
      return primary;
    }
    const ida = String((a as { id?: string })?.id ?? '');
    const idb = String((b as { id?: string })?.id ?? '');
    return ida.localeCompare(idb);
  });
}

/**
 * Builder can return the same logical article more than once:
 * - Topic filters on `topicthemepairs.topic.id` may match multiple pairs on one entry
 * - The same `data.slug` can appear twice with different `id` (duplicate CMS entries)
 * - Pagination batches can occasionally overlap (unstable server order)
 *
 * Dedupe by id first, then by normalized slug. Keep first occurrence (caller should sort first).
 */
export function dedupeBlogEntriesById(entries: unknown[]): unknown[] {
  const seenIds = new Set<string>();
  const seenSlugs = new Set<string>();
  const out: unknown[] = [];
  for (const e of entries) {
    const entry = e as { id?: string | number; data?: { slug?: string } };
    const rawId = entry?.id;
    const id = rawId != null && String(rawId).trim() !== '' ? String(rawId) : '';
    const slugRaw = entry?.data?.slug;
    const slug =
      slugRaw != null && String(slugRaw).trim() !== ''
        ? String(slugRaw).trim().toLowerCase()
        : '';

    if (id && seenIds.has(id)) {
      continue;
    }
    if (slug && seenSlugs.has(slug)) {
      continue;
    }

    if (id) {
      seenIds.add(id);
    }
    if (slug) {
      seenSlugs.add(slug);
    }
    out.push(e);
  }
  return out;
}

/**
 * Last-line dedupe for mapped UI cards (covers any edge case the API layer missed).
 */
export function dedupeArticleCardsByIdOrUrl<
  T extends { id: string; url: string; title?: string; publishedDate?: string }
>(cards: T[]): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const c of cards) {
    const id = c.id && c.id !== 'unknown' && c.id !== 'error' ? String(c.id) : '';
    const url = c.url && c.url !== '#' ? c.url : '';
    const key =
      id ? `id:${id}` : url ? `url:${url}` : `bare:${c.title ?? ''}|${c.publishedDate ?? ''}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    out.push(c);
  }
  return out;
}
